<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div ref="parent" v-on-clickaway="hideContactList" class="relative">
    <div class="text-xs text-neutral-300 min-w-[170px]">
      <div v-if="isPhoneType" :error="error" @click="showContactList">
        {{ placeholder || $t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER') }}
      </div>
      <div v-if="isEmailType && !multiple" @click="showContactList">
        {{ placeholder || $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER') }}
      </div>
      <div
        v-if="isEmailType && multiple"
        class="email-tag-input flex items-center flex-nowrap gap-2 overflow-x-auto custom-scroll"
      >
        <span
          v-for="tag in alreadySelected"
          :key="tag"
          class="semantic-tag tag-mute-01 whitespace-nowrap inline-flex items-center gap-1 !text-xs"
        >
          {{ tag }}
          <span
            v-if="tag !== nonRemovable"
            class="material-symbols-outlined flex items-center justify-center text-sm w-4 h-4 rounded-[4px] hover:bg-gray-100 hover:cursor-pointer"
            @click.stop="$emit('remove-tag', tag)"
          >
            close
          </span>
        </span>
        <div
          class="py-[5px] shrink-0 whitespace-nowrap"
          @click="showContactList"
        >
          {{ placeholder || $t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER') }}
        </div>
      </div>
    </div>
    <woot-dropdown-menu
      v-if="showContacts"
      class="search-phone-dropdown custom-scroll min-w-[300px] max-w-[300px]"
      :style="dropdownStyle"
    >
      <!-- Do not replace v-show with computed property, clickaway won't work properly  -->
      <woot-dropdown-item
        class="search-input"
        :class="
          searchContacts.length > 4
            ? 'ltr:pl-2 ltr:pr-0 rtl:pr-2 rtl:pl-0'
            : 'px-2'
        "
      >
        <div class="relative">
          <span
            class="material-symbols-outlined text-icon-primary absolute top-[50%] -translate-y-1/2 ltr:left-[20px] rtl:right-[20px]"
          >
            search
          </span>
          <woot-phone-input
            v-if="isPhoneType"
            ref="input"
            v-model="inputValue"
            class="ltr:!pl-11 ltr:!pr-1 rtl:!pr-11 rtl:!pl-1"
            :placeholder="$t('CONTACTS_PAGE.SEARCH_BUTTON')"
            :show-flag-in-selection="true"
            :dropdown-direction="'bottom'"
            :disable-country-select="true"
            :error="error"
            @blur="$emit('blur')"
          />
          <input
            v-if="isEmailType && !multiple"
            ref="input"
            v-model="inputValue"
            type="text"
            :placeholder="$t('CONTACTS_PAGE.SEARCH_BUTTON')"
            class="mb-0 search-contact-email bg-transparent focus:bg-transparent ltr:!pl-11 rtl:!pr-11 rtl:!pl-1"
            :class="showAddEmailButton ? 'ltr:!pr-9' : 'ltr:!pr-1'"
            @blur="$emit('blur')"
          />
          <!-- Email + multiple: tags + search input -->
          <input
            v-if="isEmailType && multiple"
            ref="input"
            v-model="searchQuery"
            type="text"
            :placeholder="$t('CONTACTS_PAGE.SEARCH_BUTTON')"
            class="mb-0 flex-1 min-w-[100px] search-query-input !text-xs ltr:pl-11 rtl:pr-11 rtl:pl-1"
            :class="showAddEmailButton ? 'ltr:!pr-9' : 'ltr:pr-1'"
            @keydown.enter.prevent="addCustomEmail"
            @blur="$emit('blur')"
          />
          <button
            v-if="showAddEmailButton"
            type="button"
            class="absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2 flex items-center justify-center w-7 h-7 rounded-md text-green-700 hover:bg-green-50 transition-colors cursor-pointer"
            @click.stop="addCustomEmail"
          >
            <span class="material-symbols-outlined text-xl leading-none">
              add
            </span>
          </button>
        </div>
      </woot-dropdown-item>
      <woot-dropdown-item v-if="isContactEmpty">
        <div class="px-2.5 py-3 text-center text-xs text-gray-500">
          {{ $t('CONTACTS_PAGE.LIST.404') }}
        </div>
      </woot-dropdown-item>
      <woot-dropdown-item
        v-for="contact in searchContacts"
        v-show="!alreadySelected.includes(contact.email)"
        :key="contact.id"
        class="!mb-0"
      >
        <woot-button
          variant="hollow"
          color-scheme="secondary"
          class="w-full !px-[14px] !py-3 border-none rounded-none h-16 truncate contact-item-button"
          @click="onSelected(contact)"
        >
          <contact-dropdown-item
            :thumbnail="contact.thumbnail"
            :identifier="contact.id"
            :name="contact.name"
            :phone-number="contact.phone_number"
            :email="contact.email"
            :company-name="contact.additional_attributes.company_name"
            :hide-phone="emailMode"
            :hide-company="emailMode"
          />
        </woot-button>
      </woot-dropdown-item>
    </woot-dropdown-menu>
  </div>
</template>

<script>
import ContactAPI from 'dashboard/api/contacts';
import { mixin as clickaway } from 'vue-clickaway';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';
import ContactDropdownItem from './ContactDropdownItem.vue';
import rtlMixin from 'shared/mixins/rtlMixin';

const MIN_DROPDOWN_HEIGHT = 180;
const DROPDOWN_MARGIN = 16;

export default {
  components: { ContactDropdownItem },
  mixins: [clickaway, rtlMixin],
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    field: {
      type: String,
      default: 'phone_number',
    },
    skipSelected: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    emailMode: {
      type: Boolean,
      default: false,
    },
    nonRemovable: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isRequested: false,
      showContacts: false,
      searchContacts: [],
      searchQuery: '',
      dropdownStyle: {},
    };
  },
  computed: {
    inputValue: {
      get() {
        if (this.field === 'phone_number') return this.phoneNumber;
        return this.email;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    alreadySelected() {
      if (this.email)
        return this.email
          .replaceAll(',', ' ')
          .split(' ')
          .filter(val => val);
      return '';
    },
    isEmailType() {
      return this.field === 'email';
    },
    isPhoneType() {
      return this.field === 'phone_number' || this.field.includes('phone');
    },
    isContactEmpty() {
      return this.searchContacts.length === 0;
    },
    currentEmailQuery() {
      if (this.isEmailType && this.multiple) return this.searchQuery;
      return this.inputValue;
    },
    isValidEmailQuery() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        (this.currentEmailQuery || '').trim()
      );
    },
    showAddEmailButton() {
      if (!this.isEmailType || !this.isValidEmailQuery) return false;
      const email = (this.currentEmailQuery || '').trim();
      if (this.multiple) return !this.alreadySelected.includes(email);
      return true;
    },
  },
  watch: {
    phoneNumber(newVal) {
      this.getContacts(newVal);
    },
    email(newVal) {
      if (!this.multiple) {
        this.getContacts(this.emailQuery(newVal));
      }
    },
    searchQuery(newVal) {
      if (this.isEmailType && this.multiple && newVal) {
        this.showContacts = true;
        this.getContacts(newVal);
      }
    },
    searchContacts(newVal) {
      if (newVal.length === 0) this.calculateDropdownPosition(300, 95);
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScrollRecalculate, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScrollRecalculate, true);
  },
  methods: {
    onScrollRecalculate() {
      if (this.showContacts) this.calculateDropdownPosition();
    },
    emailQuery(newVal) {
      return this.multiple ? newVal.split(',').slice(-1)[0] : newVal;
    },
    async getContacts(query) {
      this.isRequested = true;
      try {
        const {
          data: { payload },
        } = await ContactAPI.search(query, 1, 'name', '', this.field);
        this.searchContacts = payload;
      } catch (error) {
        this.showAlert(this.$t('MERGE_CONTACTS.SEARCH.ERROR_MESSAGE'));
      }
    },
    addCustomEmail() {
      const email = (this.currentEmailQuery || '').trim();
      if (!email || !this.isValidEmailQuery) return;
      if (this.alreadySelected.includes(email)) return;
      this.$emit('on-select-contact', { email, name: email });
      this.searchQuery = '';
      this.showContacts = false;
      if (this.multiple) {
        this.searchContacts = [];
        this.$refs.input.focus();
      }
    },
    onSelected(contact) {
      this.$emit('on-select-contact', contact);
      if (this.multiple) {
        if (this.isEmailType) {
          this.searchQuery = '';
          this.searchContacts = [];
        }
        this.$refs.input.focus();
        return;
      }
      this.showContacts = false;
    },
    calculateDropdownPosition(w = 300, h = 276) {
      const el = this.$refs.parent;
      if (!el) return;
      // Shrink to the room below rather than flipping up over the form.
      const spaceBelow = window.innerHeight - el.getBoundingClientRect().bottom;
      const height =
        spaceBelow >= MIN_DROPDOWN_HEIGHT
          ? Math.min(h, spaceBelow - DROPDOWN_MARGIN)
          : h;
      this.dropdownStyle = {
        ...getFixedDialogStyle(el, {
          width: w,
          height,
          gap: 4,
          align: this.isRTLView ? 'right' : 'left',
        }),
        maxHeight: `${height}px`,
        zIndex: 9999,
      };
    },
    showContactList() {
      this.$emit('focus');
      this.calculateDropdownPosition();
      this.showContacts = true;
      if (this.isEmailType && this.multiple) {
        this.getContacts(this.searchQuery);
      } else {
        this.getContacts(this.phoneNumber || this.emailQuery(this.email));
      }
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },
    hideContactList() {
      this.showContacts = false;
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .search-phone-dropdown {
    @apply border border-gray-200;
    @apply bg-white dark:border-slate-700 max-h-72 overflow-auto divide-y divide-slate-200;
    @apply rounded-lg;

    .search-input {
      @apply py-1 mb-0 sticky top-0 bg-white z-[10];
      input {
        @apply flex items-center h-10 py-2 gap-2.5 w-full max-w-full;
        align-self: stretch;
        font-size: 16px !important;
        border-radius: 8px;
        border: 1px solid #d1d5db !important;
        background: #fff;
      }
    }

    .dropdown-menu__item {
      border-top: none !important;
    }
  }
  .contact-item-button {
    &:hover {
      color: #164b35 !important;
      background-color: #f0fdf4 !important;

      .option__title,
      .option__body {
        @apply text-[#164B35];
      }
    }
  }
}

.search-query-input {
  @apply border-transparent shadow-none outline-none bg-transparent;
  &:focus {
    @apply border-transparent shadow-none outline-none bg-transparent;
  }
}
</style>
